import React, { useMemo } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { SearchableDropdown } from '../../../../components/SearchableDropdown';
import { useViewModelReady } from '../../../../hooks/useViewModel';
import {
  LOOKBACK_LABELS,
  Lookback,
  useTokenDashboardViewModel,
} from './viewModel';
import {
  CampaignUsage,
  DailyUsage,
  formatCompactNumber,
  formatUsd,
  ModelUsage,
  UsageTotals,
} from './aggregate';

const LOOKBACK_OPTIONS: Lookback[] = ['7d', '30d', '90d', 'all'];
const CHART_PURPLE = '#25286a';
const CHART_PURPLE_MUTED = '#25286a';
const CHART_PURPLE_LIGHT = '#c9cae2';

export function TokenDashboardTool({ mobileNavTrigger }: { mobileNavTrigger?: React.ReactNode }) {
  const vm = useTokenDashboardViewModel();
  useViewModelReady(vm.initialize);

  const sortedCampaigns = useMemo(
    () => [...vm.campaigns].sort((a, b) => a.name.localeCompare(b.name)),
    [vm.campaigns],
  );

  const selectedCampaignName = vm.campaignId
    ? vm.campaigns.find((c) => c.id === vm.campaignId)?.name ?? '(unknown campaign)'
    : null;

  return (
    <section className="min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Token Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            {selectedCampaignName
              ? `LLM token usage and spend for ${selectedCampaignName}.`
              : 'LLM token usage and spend across all campaigns.'}
          </p>
        </div>
        {mobileNavTrigger}
      </div>

      {vm.busy ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FilterBar
            campaignId={vm.campaignId}
            onCampaignIdChange={vm.setCampaignId}
            campaigns={sortedCampaigns}
            lookback={vm.lookback}
            onLookbackChange={vm.setLookback}
            onRefresh={() => void vm.refresh()}
            loading={vm.loading}
          />

          {vm.error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {vm.error}
            </div>
          )}

          {vm.truncated && (
            <p className="mt-4 text-xs text-amber-700">
              Showing the most recent {vm.recordLimit.toLocaleString()} calls — totals are a lower
              bound. Narrow the date range or pick a single campaign for full coverage.
            </p>
          )}

          <div className="relative mt-8">
            {vm.loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            <SummaryStrip totals={vm.summary.totals} truncated={vm.truncated} />

            <Section title="Daily spend" className="mt-10">
              <DailyChart days={vm.summary.perDay} />
            </Section>

            <Section title="By model" className="mt-10">
              <ModelTable models={vm.summary.perModel} />
            </Section>

            {!vm.campaignId && (
              <Section title="By campaign" className="mt-10">
                <CampaignTable
                  campaigns={vm.summary.perCampaign}
                  onSelect={(id) =>
                    vm.setCampaignId(id === '__none__' ? null : id)
                  }
                />
              </Section>
            )}
          </div>
        </>
      )}
    </section>
  );
}

interface FilterBarProps {
  campaignId: string | null;
  onCampaignIdChange: (id: string | null) => void;
  campaigns: { id: string; name: string }[];
  lookback: Lookback;
  onLookbackChange: (l: Lookback) => void;
  onRefresh: () => void;
  loading: boolean;
}

function FilterBar({
  campaignId,
  onCampaignIdChange,
  campaigns,
  lookback,
  onLookbackChange,
  onRefresh,
  loading,
}: FilterBarProps) {
  return (
    <div className="mt-6 flex flex-wrap items-end gap-x-6 gap-y-3">
      <div className="flex flex-col gap-1">
        <label htmlFor="token-campaign" className="text-[11px] text-gray-500">
          Campaign
        </label>
        <SearchableDropdown
          id="token-campaign"
          options={[
            { value: '', label: 'All campaigns' },
            ...campaigns.map((campaign) => ({
              value: campaign.id,
              label: campaign.name,
              searchText: `${campaign.name} ${campaign.id}`,
            })),
          ]}
          value={campaignId ?? ''}
          onChange={(value) => onCampaignIdChange(value || null)}
          placeholder="All campaigns"
          searchPlaceholder="Search campaigns"
          preserveOrder
          triggerClassName="border-0 border-b border-gray-300 bg-transparent px-0 py-1 text-sm focus:border-primary focus:outline-none focus:ring-0"
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[11px] text-gray-500">Range</span>
        <div className="flex gap-3 text-sm">
          {LOOKBACK_OPTIONS.map((opt) => {
            const active = opt === lookback;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onLookbackChange(opt)}
                className={
                  active
                    ? 'border-b-2 border-gray-900 pb-0.5 text-gray-900'
                    : 'border-b-2 border-transparent pb-0.5 text-gray-500 hover:text-gray-800'
                }
              >
                {LOOKBACK_LABELS[opt]}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <RefreshCw className="h-3.5 w-3.5" />
        )}
        Refresh
      </button>
    </div>
  );
}

// ----- Summary strip ---------------------------------------------------------

function SummaryStrip({
  totals,
  truncated,
}: {
  totals: UsageTotals;
  truncated: boolean;
}) {
  const errorPct =
    totals.callCount > 0 ? (totals.errorCount / totals.callCount) * 100 : 0;
  const avgPerCall =
    totals.callCount > 0 ? totals.costMicroUsd / totals.callCount : 0;
  return (
    <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <Stat label="Spend" value={formatUsd(totals.costMicroUsd)} />
      <Stat
        label="Calls"
        value={`${totals.callCount.toLocaleString()}${truncated ? '+' : ''}`}
      />
      <Stat
        label="Tokens"
        value={formatCompactNumber(totals.totalTokens)}
        sub={`${formatCompactNumber(totals.inputTokens)} in · ${formatCompactNumber(totals.outputTokens)} out`}
      />
      <Stat label="Avg / call" value={formatUsd(avgPerCall)} />
      <Stat
        label="Errors"
        value={
          totals.errorCount > 0
            ? `${totals.errorCount.toLocaleString()} (${errorPct.toFixed(0)}%)`
            : '0'
        }
        warning={totals.errorCount > 0}
      />
    </dl>
  );
}

function Stat({
  label,
  value,
  sub,
  warning,
}: {
  label: string;
  value: string;
  sub?: string;
  warning?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-md border bg-white p-4 shadow-sm" style={{ borderColor: 'rgb(0 0 0 / 15%)' }}>
      <dt className="text-[11px] text-gray-500">{label}</dt>
      <dd
        className={`text-lg font-semibold leading-tight tabular-nums ${
          warning ? 'text-red-600' : 'text-gray-900'
        }`}
      >
        {value}
      </dd>
      {sub && (
        <dd className="mt-0.5 text-[10px] tabular-nums text-gray-500">{sub}</dd>
      )}
    </div>
  );
}

// ----- Section heading ------------------------------------------------------

function Section({
  title,
  children,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={className}>
      <h2 className="mb-3 border-b border-gray-200 pb-1 text-xs font-medium text-gray-700">
        {title}
      </h2>
      {children}
    </section>
  );
}

// ----- Daily chart ----------------------------------------------------------

function DailyChart({ days }: { days: DailyUsage[] }) {
  if (days.length === 0) return <Empty label="No usage in this range." />;

  // Fixed viewBox; SVG scales to container width. ~8px/day at 90 days, ~100px
  // at 7 — both readable without horizontal scroll.
  const W = 720;
  const H = 160;
  const padL = 44;
  const padR = 80;
  const padT = 14;
  const padB = 18;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const max = days.reduce((m, d) => Math.max(m, d.costMicroUsd), 0);
  const total = days.reduce((s, d) => s + d.costMicroUsd, 0);
  const avg = total / days.length;
  const barW = innerW / days.length;
  const peakIdx = days.reduce(
    (best, d, i) => (d.costMicroUsd > days[best].costMicroUsd ? i : best),
    0,
  );
  const peak = days[peakIdx];

  // Range-frame: tick the endpoints, plus a midpoint when there's room.
  const tickIdxs: number[] =
    days.length <= 1
      ? [0]
      : days.length <= 14
        ? days.map((_, i) => i)
        : days.length <= 45
          ? [0, Math.floor(days.length / 4), Math.floor(days.length / 2), Math.floor((days.length * 3) / 4), days.length - 1]
          : [0, Math.floor(days.length / 2), days.length - 1];

  const avgY = max > 0 ? padT + innerH - (avg / max) * innerH : padT + innerH;

  return (
    <figure>
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full" role="img" aria-label="Daily spend">
        {/* Range-frame: y-axis labelled only at the actual data extent. */}
        <line x1={padL} y1={padT} x2={padL} y2={padT + innerH} stroke="#d1d5db" strokeWidth={1} />
        <text x={padL - 6} y={padT + 4} textAnchor="end" fontSize={10} fill="#6b7280">
          {formatUsd(max)}
        </text>
        <text x={padL - 6} y={padT + innerH} textAnchor="end" fontSize={10} fill="#6b7280">
          $0
        </text>

        <line
          x1={padL}
          y1={padT + innerH}
          x2={padL + innerW}
          y2={padT + innerH}
          stroke="#d1d5db"
          strokeWidth={1}
        />

        {avg > 0 && (
          <>
            <line
              x1={padL}
              y1={avgY}
              x2={padL + innerW}
              y2={avgY}
              stroke={CHART_PURPLE_LIGHT}
              strokeWidth={1}
              strokeDasharray="2 3"
            />
            <text x={padL + innerW + 4} y={avgY + 3} fontSize={10} fill={CHART_PURPLE_MUTED}>
              avg {formatUsd(avg)}
            </text>
          </>
        )}

        {days.map((d, i) => {
          const h = max > 0 ? (d.costMicroUsd / max) * innerH : 0;
          const x = padL + i * barW;
          return (
            <rect
              key={d.dateKey}
              x={x + barW * 0.1}
              y={padT + innerH - h}
              width={Math.max(barW * 0.8, 1)}
              height={h}
              fill={CHART_PURPLE}
            >
              <title>
                {`${d.dateKey} — ${formatUsd(d.costMicroUsd)} · ${d.callCount} calls · ${formatCompactNumber(d.totalTokens)} tok`}
              </title>
            </rect>
          );
        })}

        {peak && peak.costMicroUsd > 0 && (
          <text
            x={padL + peakIdx * barW + barW / 2}
            y={padT + innerH - (peak.costMicroUsd / max) * innerH - 3}
            textAnchor="middle"
            fontSize={10}
            fill={CHART_PURPLE}
          >
            {formatUsd(peak.costMicroUsd)}
          </text>
        )}

        {tickIdxs.map((idx) => (
          <text
            key={idx}
            x={padL + idx * barW + barW / 2}
            y={H - 4}
            textAnchor="middle"
            fontSize={9}
            fill="#9ca3af"
          >
            {days[idx].dateKey.slice(5)}
          </text>
        ))}
      </svg>
    </figure>
  );
}

// ----- Model table ----------------------------------------------------------

function ModelTable({ models }: { models: ModelUsage[] }) {
  if (models.length === 0) return <Empty label="No model usage in this range." />;
  return (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
          <th className="py-1.5 pr-3 font-normal">Model</th>
          <th className="py-1.5 pr-3 font-normal">Daily spend</th>
          <th className="py-1.5 pr-3 text-right font-normal">Calls</th>
          <th className="py-1.5 pr-3 text-right font-normal">Tokens</th>
          <th className="py-1.5 pr-3 text-right font-normal">$/M tok</th>
          <th className="py-1.5 text-right font-normal">Spend</th>
        </tr>
      </thead>
      <tbody>
        {models.map((m) => {
          // $/M tokens: micro-USD / tokens = micro-USD per token; * 1M tokens = micro-USD;
          // divide by 1M to express in USD.
          const perMillionUsd = m.totalTokens > 0 ? m.costMicroUsd / m.totalTokens : 0;
          return (
            <tr
              key={m.model}
              className="border-t border-gray-100 align-middle"
            >
              <td className="py-1.5 pr-3">
                <div className="font-medium text-gray-900">{m.model}</div>
                <div className="text-[10px] text-gray-500">{m.provider}</div>
              </td>
              <td className="py-1.5 pr-3 text-gray-700">
                <Sparkline values={m.dailyCost} />
              </td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-gray-700">
                {m.callCount.toLocaleString()}
              </td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-gray-700">
                {formatCompactNumber(m.totalTokens)}
              </td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-gray-700">
                {perMillionUsd > 0 ? `$${perMillionUsd.toFixed(2)}` : '—'}
              </td>
              <td className="py-1.5 text-right font-mono tabular-nums text-gray-900">
                {formatUsd(m.costMicroUsd)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ----- Campaign table -------------------------------------------------------

function CampaignTable({
  campaigns,
  onSelect,
}: {
  campaigns: CampaignUsage[];
  onSelect: (campaignId: string) => void;
}) {
  if (campaigns.length === 0) return <Empty label="No campaign usage in this range." />;
  const TOP = 15;
  const shown = campaigns.slice(0, TOP);
  const grandTotal = campaigns.reduce((s, c) => s + c.costMicroUsd, 0);
  return (
    <>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
            <th className="py-1.5 pr-3 font-normal">Campaign</th>
            <th className="py-1.5 pr-3 font-normal">Daily spend</th>
            <th className="py-1.5 pr-3 text-right font-normal">Calls</th>
            <th className="py-1.5 pr-3 text-right font-normal">Tokens</th>
            <th className="py-1.5 pr-3 text-right font-normal">Share</th>
            <th className="py-1.5 text-right font-normal">Spend</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((c) => {
            const sharePct = grandTotal > 0 ? (c.costMicroUsd / grandTotal) * 100 : 0;
            const isUnknown = c.campaignId === '__none__';
            return (
              <tr key={c.campaignId} className="border-t border-gray-100 align-middle">
                <td className="py-1.5 pr-3">
                  {isUnknown ? (
                    <span className="text-gray-500">(no campaign)</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSelect(c.campaignId)}
                      className="text-left font-medium text-gray-900 hover:text-primary hover:underline"
                    >
                      {c.campaignName}
                    </button>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-gray-700">
                  <Sparkline values={c.dailyCost} />
                </td>
                <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-gray-700">
                  {c.callCount.toLocaleString()}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-gray-700">
                  {formatCompactNumber(c.totalTokens)}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-gray-700">
                  {sharePct >= 1 ? `${sharePct.toFixed(0)}%` : '<1%'}
                </td>
                <td className="py-1.5 text-right font-mono tabular-nums text-gray-900">
                  {formatUsd(c.costMicroUsd)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {campaigns.length > TOP && (
        <p className="mt-2 text-[11px] text-gray-500">
          + {campaigns.length - TOP} more campaign{campaigns.length - TOP === 1 ? '' : 's'}
        </p>
      )}
    </>
  );
}

// ----- Sparkline ------------------------------------------------------------

function Sparkline({
  values,
  width = 96,
  height = 18,
}: {
  values: number[];
  width?: number;
  height?: number;
}) {
  if (values.length === 0) return <span className="text-gray-300">—</span>;
  const max = values.reduce((m, v) => Math.max(m, v), 0);
  if (max === 0) {
    return (
      <svg width={width} height={height} className="block">
        <line
          x1={0}
          y1={height - 1}
          x2={width}
          y2={height - 1}
          stroke="#ede9fe"
          strokeWidth={1}
        />
      </svg>
    );
  }
  const stepX = values.length > 1 ? width / (values.length - 1) : width;
  const pts: string[] = [];
  let lastIdx = 0;
  let lastVal = 0;
  values.forEach((v, i) => {
    const x = i * stepX;
    const y = height - (v / max) * (height - 1) - 0.5;
    pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    if (v > lastVal) {
      lastVal = v;
      lastIdx = i;
    }
  });
  const peakX = lastIdx * stepX;
  const peakY = height - (lastVal / max) * (height - 1) - 0.5;
  return (
    <svg width={width} height={height} className="block" aria-hidden>
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke={CHART_PURPLE}
        strokeWidth={1}
      />
      <circle cx={peakX} cy={peakY} r={1.5} fill={CHART_PURPLE_MUTED} />
    </svg>
  );
}

// ----- Empty state ----------------------------------------------------------

function Empty({ label }: { label: string }) {
  return <p className="py-6 text-center text-xs text-gray-400">{label}</p>;
}
